"use client";

import React from "react";
import Link from "next/link";
import { Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/ui/select";
import { Button } from "@/app/components/ui/button";
import { applyFilterUpdates } from "@/app/(pages)/(frontend)/t/[tenant]/gebrauchtwagen/filters/utils";
import type { VehicleFilterValues } from "@/app/(pages)/(frontend)/t/[tenant]/gebrauchtwagen/filters/definitions";
import type {
  VehicleConditionFilterValue,
  VehicleTypeFilterValue,
} from "@/app/(pages)/(frontend)/t/[tenant]/gebrauchtwagen/filters/value-types";
import type { VehicleQuickRow } from "@/app/(pages)/(frontend)/t/[tenant]/gebrauchtwagen/lib/fetch-vehicle-quick-data";
import type { AvailableMakesAndModels } from "@/app/(pages)/(frontend)/t/[tenant]/gebrauchtwagen/lib/fetch-available-makes-models";
import { mapBodyToVehicleType } from "@/app/(pages)/(frontend)/t/[tenant]/gebrauchtwagen/lib/vehicletype-mapping";

type VehicleQuickSearchClientProps = {
  vehicles: VehicleQuickRow[];
  availableMakesAndModels: AvailableMakesAndModels;
  defaultLocation?: string;
};

const vehicletypeLabels: Record<VehicleTypeFilterValue, string> = {
  small: "Kleinwagen",
  "station-wagon": "Kombi",
  sedan: "Limousine",
  "sports-coupe": "Sportwagen / Coupé",
  convertible: "Cabrio",
  "suv-pickup": "Geländewagen / SUV / Pickup",
  commercial: "Nutzfahrzeuge",
  camper: "Camper",
};

export default function VehicleQuickSearchClient({
  vehicles,
  availableMakesAndModels,
  defaultLocation,
}: VehicleQuickSearchClientProps) {
  const [selectedMake, setSelectedMake] = React.useState<string | null>(null);
  const [selectedModel, setSelectedModel] = React.useState<string | null>(null);
  const [selectedCondition, setSelectedCondition] =
    React.useState<VehicleConditionFilterValue | null>(null);
  const [selectedVehicleType, setSelectedVehicleType] =
    React.useState<VehicleTypeFilterValue | null>(null);

  // Filter vehicles based on selected filters
  const filteredVehicles = React.useMemo(() => {
    return vehicles.filter((vehicle) => {
      if (selectedMake && vehicle.make !== selectedMake) return false;
      if (selectedModel && vehicle.model !== selectedModel) return false;
      if (selectedCondition && vehicle.type !== selectedCondition) return false;
      if (selectedVehicleType && vehicle.body) {
        const vehicleTypes = mapBodyToVehicleType(vehicle.body);
        if (!vehicleTypes.includes(selectedVehicleType)) return false;
      }
      return true;
    });
  }, [
    vehicles,
    selectedMake,
    selectedModel,
    selectedCondition,
    selectedVehicleType,
  ]);

  // Calculate dynamic facets based on filtered vehicles
  const dynamicFacets = React.useMemo(() => {
    // Count makes
    const makeCounts: Record<string, number> = {};
    const modelCounts: Record<string, number> = {};
    const conditionCounts: Record<string, number> = {};
    const vehicletypeCounts: Record<string, number> = {};

    for (const vehicle of filteredVehicles) {
      // Count makes
      if (vehicle.make) {
        makeCounts[vehicle.make] = (makeCounts[vehicle.make] || 0) + 1;
      }
      // Count models
      if (vehicle.model) {
        modelCounts[vehicle.model] = (modelCounts[vehicle.model] || 0) + 1;
      }
      // Count conditions
      if (vehicle.type) {
        conditionCounts[vehicle.type] =
          (conditionCounts[vehicle.type] || 0) + 1;
      }
      // Count vehicletypes
      if (vehicle.body) {
        const vehicleTypes = mapBodyToVehicleType(vehicle.body);
        for (const vt of vehicleTypes) {
          vehicletypeCounts[vt] = (vehicletypeCounts[vt] || 0) + 1;
        }
      }
    }

    return {
      make: makeCounts,
      model: modelCounts,
      condition: conditionCounts,
      vehicletype: vehicletypeCounts,
    };
  }, [filteredVehicles]);

  // Get make options from dynamic facets
  const makeOptions = React.useMemo(() => {
    return availableMakesAndModels.makes
      .map((make) => ({
        value: make.id,
        label: make.label,
        count: dynamicFacets.make[make.id] ?? 0,
      }))
      .filter((make) => make.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [dynamicFacets.make, availableMakesAndModels.makes]);

  // Get model options from dynamic facets (filtered by selected make)
  const modelOptions = React.useMemo(() => {
    if (!selectedMake) return [];
    const models = availableMakesAndModels.modelsByMake[selectedMake] ?? [];
    return models
      .map((model) => ({
        value: model.id,
        label: model.label,
        count: dynamicFacets.model[model.id] ?? 0,
      }))
      .filter((model) => model.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [selectedMake, dynamicFacets.model, availableMakesAndModels.modelsByMake]);

  // Get condition options from dynamic facets
  const conditionOptions = React.useMemo(() => {
    return Object.entries(dynamicFacets.condition)
      .map(([value, count]) => ({
        value: value as VehicleConditionFilterValue,
        label: value,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [dynamicFacets.condition]);

  // Get vehicletype options from dynamic facets with correct labels
  const vehicleTypeOptions = React.useMemo(() => {
    return Object.entries(dynamicFacets.vehicletype)
      .map(([value, count]) => ({
        value: value as VehicleTypeFilterValue,
        label: vehicletypeLabels[value as VehicleTypeFilterValue] || value,
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [dynamicFacets.vehicletype]);

  const filteredCount = React.useMemo(
    () => filteredVehicles.length,
    [filteredVehicles]
  );

  const filterValues = React.useMemo<VehicleFilterValues>(
    () => ({
      search: "",
      make: selectedMake,
      model: selectedModel,
      condition: selectedCondition,
      vehicletype: selectedVehicleType ? [selectedVehicleType] : [],
      color: [],
      fuel: [],
      transmission: [],
      location: defaultLocation ? [defaultLocation] : [],
      kilometers: { min: null, max: null },
      priceRange: { min: null, max: null },
      powerRange: { min: null, max: null },
    }),
    [
      defaultLocation,
      selectedMake,
      selectedModel,
      selectedCondition,
      selectedVehicleType,
    ]
  );

  const searchHref = React.useMemo(() => {
    const params = applyFilterUpdates(new URLSearchParams(), filterValues);
    const query = params.toString();
    return query ? `/gebrauchtwagen?${query}` : "/gebrauchtwagen";
  }, [filterValues]);

  const detailsSearchHref = React.useMemo(() => {
    if (!defaultLocation) {
      return "/gebrauchtwagen";
    }

    const params = new URLSearchParams();
    params.set("location", defaultLocation);
    return `/gebrauchtwagen?${params.toString()}`;
  }, [defaultLocation]);

  return (
    <div className="pt-8 pb-4 min-[910px]:py-12">
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-6 w-6 shrink-0 items-center justify-center">
          <Search className="h-6 w-6 text-white" />
        </div>
        <h2 className="text-[20px] font-bold text-white md:text-[24px]">
          Finden Sie Ihren Traumwagen
        </h2>
      </div>

      <div className="space-y-2">
        {/* First Row: Alle Marken and Alle Modelle */}
        <div className="grid grid-cols-2 gap-2 min-[910px]:grid-cols-3">
          <Select
            value={selectedMake ?? undefined}
            onValueChange={(value) => {
              setSelectedMake(value === "all" ? null : value);
              setSelectedModel(null);
            }}
          >
            <SelectTrigger
              className="min-h-11 w-full border-0 bg-white text-sm text-gray-900 shadow-none focus:ring-0"
              aria-label="Marke auswählen"
            >
              <SelectValue placeholder="Marke auswählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Marken</SelectItem>
              {makeOptions.map((make) => (
                <SelectItem key={make.value} value={make.value}>
                  {make.label} ({make.count ?? 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            disabled={!selectedMake || modelOptions.length === 0}
            value={selectedModel ?? undefined}
            onValueChange={(value) => {
              setSelectedModel(value === "all" ? null : value);
            }}
          >
            <SelectTrigger
              className="min-h-11 w-full border-0 bg-white text-sm text-gray-900 shadow-none focus:ring-0 min-[910px]:h-12"
              aria-label="Modell auswählen"
            >
              <SelectValue placeholder="Modell auswählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Modelle</SelectItem>
              {modelOptions.map((model) => (
                <SelectItem key={model.value} value={model.value}>
                  {model.label} ({model.count ?? 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            asChild
            className="hidden max-h-11 w-full gap-1.5 border-0 bg-white px-4 text-sm font-normal text-gray-900 shadow-none hover:bg-gray-100 focus:ring-0 min-[910px]:flex"
          >
            <Link href={searchHref}>
              Fahrzeuge suchen
              <span className="hidden min-[990px]:inline">
                ({filteredCount})
              </span>
            </Link>
          </Button>
        </div>

        {/* Second Row: Alle Zustände and Alle Kategorien */}
        <div className="grid grid-cols-2 gap-2 min-[910px]:grid-cols-3">
          <Select
            value={selectedCondition ?? undefined}
            onValueChange={(value) =>
              setSelectedCondition(
                value === "all" ? null : (value as VehicleConditionFilterValue)
              )
            }
          >
            <SelectTrigger
              className="min-h-11 w-full border-0 bg-white text-sm text-gray-900 shadow-none focus:ring-0 min-[910px]:h-12"
              aria-label="Zustand auswählen"
            >
              <SelectValue placeholder="Zustand auswählen" />
            </SelectTrigger>
            <SelectContent>
              {conditionOptions.map((condition) => (
                <SelectItem key={condition.value} value={condition.value}>
                  {condition.label} ({condition.count ?? 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={selectedVehicleType ?? undefined}
            onValueChange={(value) =>
              setSelectedVehicleType(
                value === "all" ? null : (value as VehicleTypeFilterValue)
              )
            }
          >
            <SelectTrigger
              className="min-h-11 w-full border-0 bg-white text-sm text-gray-900 shadow-none focus:ring-0 min-[910px]:h-12"
              aria-label="Kategorie auswählen"
            >
              <SelectValue placeholder="Kategorie auswählen" />
            </SelectTrigger>
            <SelectContent>
              {vehicleTypeOptions.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label} ({type.count ?? 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="hidden min-[910px]:block">
            <div className="mt-2 flex justify-start">
              <Link
                href={detailsSearchHref}
                className="text-sm text-white underline"
              >
                Zur Detailsuche
              </Link>
            </div>
          </div>
        </div>

        {/* Button and Link Row for Mobile */}
        <div className="flex flex-col gap-2 min-[910px]:hidden">
          <Button
            asChild
            className="max-h-11 w-full gap-1.5 border-0 bg-white px-4 text-sm font-normal text-gray-900 shadow-none hover:bg-gray-100 focus:ring-0"
          >
            <Link href={searchHref}>Fahrzeuge suchen ({filteredCount})</Link>
          </Button>
          <div className="flex justify-start">
            <Link href={detailsSearchHref} className="text-white underline">
              Zur Detailsuche
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
